import {
  Asset as XdrAsset,
  Claimant,
  CreateClaimableBalanceOp,
  Int64,
  Operation,
  OperationBody,
} from "../../xdr/index.js";
import { Asset } from "../asset.js";
import { CreateClaimableBalanceOpts, OperationAttributes } from "./types.js";
import {
  constructAmountRequirementsError,
  isValidAmount,
  setSourceAccount,
  toXdrAmount,
} from "../util/operations.js";

/**
 * Create a new claimable balance operation.
 *
 *
 * @param opts - Options object
 *   - `asset`: The asset for the claimable balance.
 *   - `amount`: Amount.
 *   - `claimants`: An array of Claimants
 *   - `source`: The source account for the operation. Defaults to the transaction's source account.
 *
 * @example
 * ```ts
 * const asset = new Asset(
 *   'USD',
 *   'GDGU5OAPHNPU5UCLE5RDJHG7PXZFQYWKCFOEXSXNMR6KRQRI5T6XXCD7'
 * );
 * const amount = '100.0000000';
 * const claimants = [
 *   new Claimant(
 *     'GCEZWKCA5VLDNRLN3RPRJMRZOX3Z6G5CHCGSNFHEYVXM3XOJMDS674JZ',
 *      Claimant.predicateBeforeAbsoluteTime("4102444800000")
 *   )
 * ];
 *
 * const op = Operation.createClaimableBalance({
 *   asset,
 *   amount,
 *   claimants
 * });
 * ```
 */
export function createClaimableBalance(
  opts: CreateClaimableBalanceOpts,
): Operation {
  if (!(opts.asset instanceof Asset)) {
    throw new Error(
      "must provide an asset for create claimable balance operation",
    );
  }

  if (!isValidAmount(opts.amount)) {
    throw new TypeError(constructAmountRequirementsError("amount"));
  }

  if (!Array.isArray(opts.claimants) || opts.claimants.length === 0) {
    throw new Error("must provide at least one claimant");
  }

  const asset: XdrAsset = opts.asset.toXdrObject();
  const amount: Int64 = toXdrAmount(opts.amount);
  const claimants: Claimant[] = opts.claimants.map((c) => c.toXdrObject());

  const createClaimableBalanceOp = new CreateClaimableBalanceOp({
    asset,
    amount,
    claimants,
  });

  const opAttributes: OperationAttributes = {
    sourceAccount: null,
    body: OperationBody.createClaimableBalance(createClaimableBalanceOp),
  };

  setSourceAccount(opAttributes, opts);

  return new Operation(opAttributes);
}
